import React, { useContext } from 'react';
import { ThemeContext } from '../../hooks/ThemeContext';
import ProtectedImage from './ProtectedImage';
import { Images } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/formatters';

// Ajout du support multi-images : images = tableau d'urls (optionnel)
const ProductCard = ({ 
  title, 
  description, 
  price, 
  promoPrice,
  promoActive,
  promoDiscount,
  image, // fallback
  images, // tableau d'urls (optionnel)
  category, 
  meta,
  onAddToCart,
  onViewDetails,
  onViewGallery,
  productId
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const cardBase = isDark
    ? 'bg-white/5 border border-white/10 hover:bg-white/[0.07] shadow-xl'
    : 'bg-white border border-gray-200 hover:shadow-2xl';

  // Gestion images multiples (priorité à images[] sinon fallback image)
  const imagesArray = Array.isArray(images) && images.length > 0
    ? images.filter(Boolean)
    : image ? [image] : [];

  return (
    <div className={`${cardBase} rounded-3xl overflow-hidden transition-all group relative hover:-translate-y-1`}>
      {/* Mini-carousel ou badge multi-images */}
      <div className={`relative h-56 overflow-hidden ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'bg-gray-100'}`}>
        {imagesArray.length > 1 ? (
          <div className="w-full h-full flex items-center overflow-x-auto no-scrollbar gap-1 p-2">
            {imagesArray.slice(0, 3).map((img, idx) => (
              <ProtectedImage
                key={idx}
                imagePath={img}
                alt={title + ' - vue ' + (idx + 1)}
                className="h-full w-1/3 object-cover rounded-2xl border border-white/10 mx-0.5"
                showWatermark={true}
                hoverPreview={true}
                hoverPreviewScale={1.28}
              />
            ))}
            {imagesArray.length > 3 && (
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full z-10 shadow-xl">
                +{imagesArray.length - 3} vues
              </span>
            )}
          </div>
        ) : imagesArray.length === 1 ? (
          <ProtectedImage
            imagePath={imagesArray[0]}
            alt={title}
            className="w-full h-full"
            objectFit="cover"
            showWatermark={true}
            hoverPreview={true}
            hoverPreviewScale={1.24}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className={`w-16 h-16 ${isDark ? 'text-white/10' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {imagesArray.length > 1 && (
          <span className="absolute top-3 left-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full z-10 shadow-xl">
            {imagesArray.length} vues
          </span>
        )}
        {category && (
          <span className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-medium z-10 shadow-xl ${isDark ? 'bg-rose-500/90 text-white' : 'bg-rose-600 text-white'}`}>
            {category}
          </span>
        )}
      </div>

      {/* Contenu */}
      <div className="p-5 md:p-6">
        <h3 className="text-lg font-semibold mb-2 leading-7">{title}</h3>
        <p className={`text-sm mb-4 leading-7 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {description}
        </p>

        {meta && (
          <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {meta}
          </p>
        )}

        {/* Prix et Actions */}
        <div className="flex items-end justify-between gap-4">
          <div className="flex flex-col">
            {promoActive && promoPrice !== null ? (
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                  {formatCurrency(promoPrice)}
                </span>
                <span className={`text-sm line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {formatCurrency(price)}
                </span>
              </div>
            ) : (
              <span className={`text-2xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                {formatCurrency(price)}
              </span>
            )}
            {promoActive && promoDiscount ? (
              <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                -{formatPercent(promoDiscount, 0)}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {onViewGallery && (
              <button
                onClick={() => onViewGallery(productId)}
                className={`p-3 rounded-2xl text-sm font-medium shadow-xl ${isDark ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'}`}
                title="Voir la galerie des créations"
              >
                <Images className="w-4 h-4" />
              </button>
            )}
            {onViewDetails && (
              <button
                onClick={onViewDetails}
                className={`px-4 py-3 rounded-2xl text-sm font-medium shadow-xl ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                Détails
              </button>
            )}
            {onAddToCart && (
              <button
                onClick={onAddToCart}
                className={`px-4 py-3 rounded-2xl text-sm font-semibold shadow-xl ${isDark ? 'bg-rose-500 hover:bg-rose-600' : 'bg-rose-600 hover:bg-rose-700'} text-white`}
              >
                Ajouter
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
